import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./AdminForm.css";
import { API_BASE_URL } from "./apiConfig";

const emptyItem = () => ({ productId: "", productName: "", qty: "", rate: "", amount: 0 });
const emptyCharge = () => ({ chargeName: "", isPercentage: false, rateOrValue: "" });

const PartyTransaction = () => {
  const [parties, setParties] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState("");
  const [products, setProducts] = useState([]);

  const [chargeTypes, setChargeTypes] = useState([]);
  const [charges, setCharges] = useState([]);

  const [txnDate, setTxnDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNo, setInvoiceNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Load parties + charge types once
  useEffect(() => {
    fetch(`${API_BASE_URL}/party`).then((r) => r.json()).then(setParties);
    fetch(`${API_BASE_URL}/party/chargetypes`).then((r) => r.json()).then(setChargeTypes);
  }, []);

  // Load products whenever selected party changes
  useEffect(() => {
    if (!selectedPartyId) { setProducts([]); return; }
    fetch(`${API_BASE_URL}/product?partyId=${selectedPartyId}`)
      .then((r) => r.json())
      .then(setProducts);
  }, [selectedPartyId]);

  // ---- Line items ----
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === "productId") {
      const chosen = products.find((p) => String(p.productId) === String(value));
      updated[index].productName = chosen ? chosen.productName : "";
      updated[index].rate = chosen ? chosen.defaultRate : updated[index].rate;
    }

    const qty = parseFloat(updated[index].qty) || 0;
    const rate = parseFloat(updated[index].rate) || 0;
    updated[index].amount = +(qty * rate).toFixed(2);
    setItems(updated);
  };

  const addItemRow = () => setItems([...items, emptyItem()]);
  const removeItemRow = (i) => {
    if (items.length === 1) return;
    setItems(items.filter((_, idx) => idx !== i));
  };

  // ---- Charges ----
  const addChargeRow = () => setCharges([...charges, emptyCharge()]);
  const removeChargeRow = (i) => setCharges(charges.filter((_, idx) => idx !== i));

  const updateCharge = (index, field, value) => {
    const updated = [...charges];
    updated[index][field] = value;

    // When a charge-type is picked from the dropdown, prefill its % flag and default value
    if (field === "chargeName") {
      const chosen = chargeTypes.find((c) => c.chargeName === value);
      if (chosen) {
        updated[index].isPercentage = chosen.isPercentage;
        updated[index].rateOrValue = chosen.defaultValue;
      }
    }
    setCharges(updated);
  };

  // ---- Totals ----
  const taxable = items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  const chargeAmount = (c) => {
    const val = parseFloat(c.rateOrValue) || 0;
    return c.isPercentage ? +((taxable * val) / 100).toFixed(2) : val;
  };

  const totalCharges = charges.reduce((sum, c) => sum + chargeAmount(c), 0);
  const grandTotal = +(taxable + totalCharges).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPartyId) { setMessage({ type: "error", text: "Please select a party." }); return; }
    if (items.some((i) => !i.productId)) { setMessage({ type: "error", text: "Please select a product for every row." }); return; }

    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE_URL}/party/transaction`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partyId: parseInt(selectedPartyId, 10),
          txnDate,
          invoiceNo,
          remarks,
          items: items.map((i) => ({
            productId: parseInt(i.productId, 10),
            productName: i.productName,
            qty: parseFloat(i.qty) || 0,
            rate: parseFloat(i.rate) || 0,
            amount: parseFloat(i.amount) || 0,
          })),
          charges: charges
            .filter((c) => c.chargeName)
            .map((c) => ({
              chargeName: c.chargeName,
              isPercentage: !!c.isPercentage,
              rateOrValue: parseFloat(c.rateOrValue) || 0,
            })),
        }),
      });

      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setMessage({ type: "success", text: `Saved successfully. Transaction ID: ${data.txnId}` });
      setItems([emptyItem()]);
      setCharges([]);
      setInvoiceNo("");
      setRemarks("");
    } catch (err) {
      setMessage({ type: "error", text: "Something went wrong while saving. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Party Transaction Entry</h1>
      </div>

      <div className="content-card">
        <div className="form-wrapper">
          {message.text && (
            <div className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="form-grid">
              <div className="form-group">
                <label>Party / Company *</label>
                <select
                  className="form-control"
                  value={selectedPartyId}
                  onChange={(e) => {
                    setSelectedPartyId(e.target.value);
                    setItems([emptyItem()]);
                  }}
                  required
                >
                  <option value="">-- Select Party --</option>
                  {parties.map((p) => (
                    <option key={p.partyId} value={p.partyId}>
                      {p.partyName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={txnDate}
                  onChange={(e) => setTxnDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Invoice No</label>
                <input
                  className="form-control"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                />
              </div>
            </div>

            {/* Line items */}
            <h3 style={{ marginTop: "2rem", marginBottom: "12px", color: "#0f172a", fontSize: "1.05rem" }}>
              Items / Daily Supply
            </h3>
            <table className="line-item-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <select
                        value={item.productId}
                        onChange={(e) => updateItem(i, "productId", e.target.value)}
                        disabled={!selectedPartyId}
                        required
                      >
                        <option value="">-- Select Product --</option>
                        {products.map((p) => (
                          <option key={p.productId} value={p.productId}>
                            {p.productName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateItem(i, "qty", e.target.value)}
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(i, "rate", e.target.value)}
                        required
                      />
                    </td>
                    <td style={{ fontWeight: 600, color: "#0f172a" }}>{item.amount}</td>
                    <td>
                      <button type="button" onClick={() => removeItemRow(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="btn-secondary-sm"
              onClick={addItemRow}
              disabled={!selectedPartyId}
            >
              + Add Item
            </button>
            {!selectedPartyId && (
              <p style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "8px" }}>
                Select a party first to choose its products.
              </p>
            )}

            {/* Charges */}
            <h3 style={{ marginTop: "2rem", marginBottom: "12px", color: "#0f172a", fontSize: "1.05rem" }}>
              Additional Charges (GST, Tempo, Labour, etc.)
            </h3>
            <table className="line-item-table">
              <thead>
                <tr>
                  <th>Charge</th>
                  <th>Type</th>
                  <th>Rate / Value</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c, i) => (
                  <tr key={i}>
                    <td>
                      <select
                        value={c.chargeName}
                        onChange={(e) => updateCharge(i, "chargeName", e.target.value)}
                        required
                      >
                        <option value="">-- Select Charge --</option>
                        {chargeTypes.map((ct) => (
                          <option key={ct.chargeTypeId} value={ct.chargeName}>
                            {ct.chargeName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{c.isPercentage ? "% of taxable" : "Fixed amount"}</td>
                    <td>
                      <input
                        type="number"
                        value={c.rateOrValue}
                        onChange={(e) => updateCharge(i, "rateOrValue", e.target.value)}
                        placeholder={c.isPercentage ? "e.g. 18" : "e.g. 500"}
                      />
                    </td>
                    <td style={{ fontWeight: 600, color: "#0f172a" }}>{chargeAmount(c)}</td>
                    <td>
                      <button type="button" onClick={() => removeChargeRow(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn-secondary-sm" onClick={addChargeRow}>
              + Add Charge
            </button>

            <div className="form-group" style={{ marginTop: "1.5rem" }}>
              <label>Remarks</label>
              <textarea
                className="form-control"
                rows={3}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            {/* Totals */}
            <div className="totals-box">
              <p>Taxable Amount: <strong>{taxable.toFixed(2)}</strong></p>
              <p>Total Charges: <strong>{totalCharges.toFixed(2)}</strong></p>
              <p className="grand-total">Grand Total: <strong>{grandTotal}</strong></p>
            </div>

            <button type="submit" className="btn-submit" disabled={submitting}>
              {submitting ? "Saving..." : "Submit Transaction"}
            </button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PartyTransaction;